import { authAPI } from './api.js';

// Environment configuration
const IS_PRODUCTION = import.meta.env.PROD;
const IS_DEVELOPMENT = import.meta.env.DEV;

// Secure token storage configuration
const STORAGE_TYPE = IS_PRODUCTION ? 'localStorage' : 'localStorage';
const TOKEN_KEY = 'srpf_auth_token';
const USER_KEY = 'srpf_user_data';

// Token management with security features
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error reading token from storage:', error);
    return null;
  }
};

export const setToken = (token) => {
  try {
    // In production, consider additional security measures
    if (IS_PRODUCTION && token) {
      // You could add token validation here
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const expiry = tokenData.exp * 1000;
      if (Date.now() > expiry) {
        console.warn('Token is expired');
        return false;
      }
    }
    window[STORAGE_TYPE].setItem(TOKEN_KEY, token);
    return true;
  } catch (error) {
    console.error('Error storing token:', error);
    return false;
  }
};

export const removeToken = () => {
  try {
    window[STORAGE_TYPE].removeItem(TOKEN_KEY);
    return true;
  } catch (error) {
    console.error('Error removing token:', error);
    return false;
  }
};

// User management with validation
export const getUser = () => {
  try {
    const user = window[STORAGE_TYPE].getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error reading user from storage:', error);
    return null;
  }
};

export const setUser = (user) => {
  try {
    if (user && typeof user === 'object') {
      window[STORAGE_TYPE].setItem(USER_KEY, JSON.stringify(user));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error storing user:', error);
    return false;
  }
};

export const removeUser = () => {
  try {
    window[STORAGE_TYPE].removeItem(USER_KEY);
    return true;
  } catch (error) {
    console.error('Error removing user:', error);
    return false;
  }
};

export const isAuthenticated = () => {
  return !!getToken();
};

export const login = async (credentials) => {
  try {
    const response = await authAPI.login(credentials);
    const { token, user } = response.data;
    
    setToken(token);
    setUser(user);
    
    return { success: true, user };
  } catch (error) {
    return { 
      success: false, 
      error: error.response?.data?.message || 'Login failed' 
    };
  }
};

export const logout = async () => {
  try {
    await authAPI.logout();
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    removeToken();
    removeUser();
    window.location.href = '/login';
  }
};

export const refreshAuth = async () => {
  try {
    const response = await authAPI.me();
    const { user } = response.data;
    setUser(user);
    return { success: true, user };
  } catch (error) {
    removeToken();
    removeUser();
    return { success: false, error: 'Session expired' };
  }
};
